import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Sparkles, Check, ArrowRight, ArrowLeft, Zap, TrendingUp, Crown, Building2 } from 'lucide-react';
import api from '../utils/api';

const PricingPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('annual');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansRes, settingsRes] = await Promise.all([
          api.get('/subscription/plans'),
          api.get('/cms/public/site-settings')
        ]);
        setPlans(plansRes.data);
        
        // Process settings to convert relative URLs to absolute
        const processedSettings = { ...settingsRes.data };
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        
        // Helper to convert URLs
        const convertUrl = (url) => {
          if (!url) return url;
          if (url.startsWith('/api')) return `${backendUrl}${url}`;
          if (url.includes('drive.google.com/file/d/')) {
            const fileId = url.match(/\/d\/([^/]+)/)?.[1];
            if (fileId) return `https://drive.google.com/uc?export=view&id=${fileId}`;
          }
          return url;
        };
        
        processedSettings.logo_url = convertUrl(processedSettings.logo_url);
        setSettings(processedSettings);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatPrice = (priceInPaise) => {
    if (!priceInPaise || priceInPaise === 0) return 'Free';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(priceInPaise / 100);
  };

  const getMonthlyEquivalent = (plan) => {
    if (billingCycle === 'annual') {
      return Math.round((plan.price_annual || 0) / 12);
    }
    return plan.price_monthly || 0;
  };

  const getAnnualPrice = (plan) => {
    return plan.price_annual || 0;
  };

  const getPlanIcon = (slug) => {
    switch (slug) {
      case 'starter': return Zap;
      case 'growth': return TrendingUp;
      case 'pro': return Crown;
      case 'enterprise': return Building2;
      default: return Sparkles;
    }
  };

  const siteName = settings?.site_name || 'Ontrof';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900" data-testid="pricing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              {settings?.logo_url ? (
                <img src={settings.logo_url} alt={siteName} className="h-10 w-auto" />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
              )}
              <span className="text-xl font-bold text-white">{siteName}</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/features" className="text-white/80 hover:text-white text-sm font-medium transition-colors">Features</Link>
              <Link to="/pricing" className="text-white text-sm font-medium transition-colors">Pricing</Link>
              <Link to="/contact" className="text-white/80 hover:text-white text-sm font-medium transition-colors">Contact</Link>
            </div>

            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/register')}
                data-testid="nav-register-btn"
                className="bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-500 hover:to-blue-600 text-white font-semibold px-5 py-2 rounded-lg shadow-lg shadow-cyan-500/25 transition-all duration-300 hover:shadow-cyan-500/40 hover:scale-105"
              >
                Register
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                data-testid="nav-login-btn"
                className="border-2 border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-slate-900 font-semibold px-5 py-2 rounded-lg transition-all duration-300 hover:scale-105"
              >
                Login
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-32 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <Button 
            variant="ghost" 
            className="mb-6 text-slate-400 hover:text-white"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-8">
            Choose the plan that works best for your travel agency. No hidden fees, no surprises.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-3 p-1.5 bg-white/10 rounded-full">
            <button
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                billingCycle === 'monthly' 
                  ? 'bg-white text-slate-900' 
                  : 'text-white/80 hover:text-white'
              }`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                billingCycle === 'annual' 
                  ? 'bg-white text-slate-900' 
                  : 'text-white/80 hover:text-white'
              }`}
              onClick={() => setBillingCycle('annual')}
            >
              Yearly
              <Badge className="bg-green-500 text-white text-[10px]">2 months free</Badge>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, idx) => {
              const isPopular = plan.slug === 'growth' || idx === 1;
              const Icon = getPlanIcon(plan.slug);
              
              return (
                <div 
                  key={plan.id || idx}
                  className={`relative rounded-2xl p-8 border transition-all duration-300 ${
                    isPopular 
                      ? 'bg-gradient-to-br from-blue-600 to-cyan-500 border-transparent scale-105 shadow-2xl shadow-blue-500/25' 
                      : 'bg-white/5 border-white/10 hover:border-blue-500/50'
                  }`}
                  data-testid={`pricing-plan-${plan.slug}`}
                >
                  {isPopular && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white">
                      Most Popular
                    </Badge>
                  )}

                  {/* Plan Icon */}
                  <div className={`w-14 h-14 mx-auto rounded-xl flex items-center justify-center mb-4 ${
                    isPopular ? 'bg-white/20' : 'bg-blue-500/10'
                  }`}>
                    <Icon className={`w-7 h-7 ${isPopular ? 'text-white' : 'text-blue-400'}`} />
                  </div>
                  
                  <div className="text-center mb-6">
                    <h3 className={`text-xl font-bold mb-2 ${isPopular ? 'text-white' : 'text-white'}`}>
                      {plan.name}
                    </h3>
                    {plan.description && (
                      <p className={`text-sm ${isPopular ? 'text-blue-100' : 'text-slate-400'}`}>
                        {plan.description}
                      </p>
                    )}
                  </div>

                  <div className="text-center mb-6">
                    <div className={`text-4xl font-bold ${isPopular ? 'text-white' : 'text-white'}`}>
                      {formatPrice(getMonthlyEquivalent(plan))}
                    </div>
                    <div className={`text-sm ${isPopular ? 'text-blue-100' : 'text-slate-400'}`}>
                      /month
                    </div>
                    {billingCycle === 'annual' && (
                      <div className={`text-xs mt-1 ${isPopular ? 'text-blue-200' : 'text-slate-500'}`}>
                        Billed {formatPrice(getAnnualPrice(plan))} annually
                      </div>
                    )}
                  </div>

                  <ul className="space-y-3 mb-8">
                    {(plan.features || []).slice(0, 7).map((feature, fidx) => (
                      <li key={fidx} className="flex items-start gap-3">
                        <Check className={`w-5 h-5 flex-shrink-0 ${isPopular ? 'text-white' : 'text-green-400'}`} />
                        <span className={`text-sm ${isPopular ? 'text-white' : 'text-slate-300'}`}>
                          {feature.name}
                          {feature.limit_value !== null && feature.limit_value !== -1 && (
                            <span className="opacity-70"> ({feature.limit_value})</span>
                          )}
                          {feature.limit_value === -1 && (
                            <span className="opacity-70"> (Unlimited)</span>
                          )}
                        </span>
                      </li>
                    ))}
                    {(plan.features || []).length > 7 && (
                      <li className={`text-sm ${isPopular ? 'text-blue-200' : 'text-blue-400'}`}>
                        +{plan.features.length - 7} more features
                      </li>
                    )}
                  </ul>

                  <Button 
                    className={`w-full ${
                      isPopular 
                        ? 'bg-white text-blue-600 hover:bg-blue-50' 
                        : 'bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 text-white'
                    }`}
                    onClick={() => navigate('/register')}
                  >
                    {plan.trial_available ? 'Start Trial' : 'Subscribe'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ or CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Need a custom plan?
          </h2>
          <p className="text-slate-400 mb-6">
            For large DMCs and enterprise clients, we offer custom pricing with dedicated support and tailored features.
          </p>
          <Button 
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            onClick={() => navigate('/contact')}
          >
            Contact Sales
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-4 sm:px-6 lg:px-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-white font-semibold">{siteName}</span>
            </Link>
            <div className="text-slate-400 text-sm">
              &copy; {settings?.copyright_text || '2026 Ontrof. All rights reserved.'}
            </div>
            <div className="flex items-center gap-6">
              <Link to="/features" className="text-slate-400 hover:text-white text-sm transition-colors">Features</Link>
              <Link to="/pricing" className="text-slate-400 hover:text-white text-sm transition-colors">Pricing</Link>
              <Link to="/contact" className="text-slate-400 hover:text-white text-sm transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
